const hasQualityGrowthGoal = (goal) => {
  if (!goal || typeof goal !== 'string') return false;
  
  // Check if the response is substantial (more than just a few words)
  if (goal.split(' ').length < 3) return false;
  
  // Look for specific keywords that indicate a well-thought-out goal
  const qualityIndicators = [
    'launch', 'scale', 'grow', 'expand', 'develop', 'increase',
    'improve', 'optimize', 'streamline', 'revenue', 'client',
    'customer', 'market', 'service', 'product', 'team'
  ];

  const highValueIndicators = [
    'revenue', 'profit', 'scale', '6 figure', '7 figure',
    'expansion', 'market share', 'acquisition'
  ];
  
  const hasQualityIndicator = qualityIndicators.some(indicator => 
    goal.toLowerCase().includes(indicator.toLowerCase())
  );

  const hasHighValueIndicator = highValueIndicators.some(indicator => 
    goal.toLowerCase().includes(indicator.toLowerCase())
  );

  return {
    isQuality: hasQualityIndicator,
    isHighValue: hasHighValueIndicator,
    score: hasHighValueIndicator ? 3 : (hasQualityIndicator ? 2 : 0)
  };
};

const hasQualityChallenge = (challenge) => {
  if (!challenge || typeof challenge !== 'string') return false;
  
  // Check for substantial response
  if (challenge.split(' ').length < 10) return false;
  
  // Look for specific keywords that indicate relevant challenges
  const challengeIndicators = [
    'strategy', 'align', 'consistent', 'workflow', 'process',
    'time', 'manual', 'scattered', 'fragmented', 'duplicate',
    'outdated', 'complex', 'difficult', 'struggle', 'inefficient',
    'team', 'communication', 'coordination', 'scale'
  ];

  const highPriorityIndicators = [
    'strategy', 'alignment', 'consistency', 'efficiency',
    'scaling', 'growth', 'team coordination', 'workflow'
  ];

  const painPointIndicators = [
    'frustrated', 'struggling', 'difficult', 'challenge',
    'problem', 'inefficient', 'waste', 'lost', 'confusing'
  ];
  
  const hasRelevantChallenge = challengeIndicators.some(indicator => 
    challenge.toLowerCase().includes(indicator.toLowerCase())
  );

  const hasHighPriority = highPriorityIndicators.some(indicator => 
    challenge.toLowerCase().includes(indicator.toLowerCase())
  );

  const hasPainPoint = painPointIndicators.some(indicator => 
    challenge.toLowerCase().includes(indicator.toLowerCase())
  );

  return {
    isRelevant: hasRelevantChallenge,
    isHighPriority: hasHighPriority,
    hasPainPoint: hasPainPoint,
    score: (hasHighPriority ? 2 : 0) + (hasPainPoint ? 1 : 0) + (hasRelevantChallenge ? 1 : 0)
  };
};

const isTargetBusinessType = (type) => {
  const targetTypes = {
    'Solopreneur': 2,
    'Small Team (2-10)': 3,
    'Agency': 2,
    'Other': 0
  };

  return {
    isTarget: targetTypes[type] > 0,
    score: targetTypes[type] || 0
  };
};

const hasRelevantTools = (tools) => {
  if (!Array.isArray(tools) || tools.length === 0) return false;
  
  const toolScores = {
    'Documents/spreadsheets': 2,
    'Note-taking apps': 2,
    'Project management tools': 1,
    'AI writing tools': 1,
    'Other': 0
  };
  
  const score = tools.reduce((total, tool) => total + (toolScores[tool] || 0), 0);
  
  return {
    hasRelevant: score > 0,
    score: Math.min(score, 3) // Cap at 3 points
  };
};

const hasAdequateAIExperience = (experience) => {
  const experienceScores = {
    'Advanced AI user': 3,
    'Using basic AI writing tools': 2,
    'Just starting to explore AI tools': 1,
    'Not using AI yet': 0
  };

  return {
    isAdequate: experienceScores[experience] >= 2,
    score: experienceScores[experience] || 0
  };
};

export const evaluateApplication = (responses) => {
  let score = {
    immediate: 0,
    waitlist: 0,
    review: 0
  };
  
  // Evaluate business type
  const businessTypeResult = isTargetBusinessType(responses.businessType);
  if (businessTypeResult.isTarget) {
    score.immediate += businessTypeResult.score;
  } else {
    score.waitlist += 2;
  }
  
  // Evaluate commitment
  if (responses.timeCommitment === 'Yes') {
    score.immediate += 2;
  } else {
    score.waitlist += 2;
  }
  
  // Evaluate tools
  const toolsResult = hasRelevantTools(responses.currentTools);
  if (toolsResult.hasRelevant) {
    score.immediate += toolsResult.score;
  }
  
  // Evaluate AI experience
  const aiResult = hasAdequateAIExperience(responses.aiExperience);
  if (aiResult.isAdequate) {
    score.immediate += aiResult.score;
  } else if (responses.aiExperience === 'Just starting to explore AI tools') {
    score.review += 1;
  } else {
    score.waitlist += 1;
  }
  
  // Evaluate growth goal
  const growthResult = hasQualityGrowthGoal(responses.growthFocus);
  if (growthResult.isQuality) {
    score.immediate += growthResult.score;
  }
  
  // Evaluate challenges
  const challengeResult = hasQualityChallenge(responses.biggestChallenge);
  if (challengeResult.isRelevant) {
    score.immediate += challengeResult.score;
  }
  
  // Special case handling for high-potential candidates
  if (growthResult.isHighValue && challengeResult.isHighPriority) {
    score.immediate += 2;
  }

  // Special case handling for "Other" business types with strong responses
  if (responses.businessType === 'Other' && 
      growthResult.isHighValue && 
      challengeResult.isHighPriority) {
    score.review += 3;
  }
  
  // Determine final routing with adjusted thresholds
  if (score.immediate >= 10) {
    return {
      result: 'CALENDLY',
      score,
      message: `
        Thank you for applying to join Moogul's beta program! Based on your responses, 
        we'd love to get you started right away.
        
        During your onboarding call, we'll:
        - Set up your Strategy Studio
        - Customize it to your workflow
        - Define your first growth initiative
        - Answer any questions
        
        Can't wait to help you transform your strategy into action!
      `
    };
  }
  
  if (score.immediate <= 5 && score.review < 3) {
    return {
      result: 'WAITLIST',
      score,
      message: `
        Thanks for your interest in Moogul! While we're focusing our beta on specific 
        use cases, we'd love to keep you updated on our progress.
        
        You'll get:
        - Early access when we expand
        - Founding member pricing
        - Strategy insights newsletter
        - Product development updates
      `
    };
  }
  
  return {
    result: 'REVIEW',
    score,
    message: `
      Thanks for your detailed application! We'd like to learn more about your needs 
      to ensure Moogul is the right fit.
      
      Expected response: Within 24 hours
      
      In the meantime:
      - Join our waitlist for updates
      - Check out our strategy resources
      - Save your founding member discount
    `
  };
}; 